import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { TrendingUp, Activity, Globe, AlertTriangle } from 'lucide-react';

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard`);
      setData(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const COLORS = ['#00FF9D', '#00F0FF', '#FFD60A', '#FF3B30'];

  if (!data) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-zinc-400 font-mono">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="analytics-dashboard">
      <div className="glass-panel border-b border-white/10 p-6">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>ANALYTICS DASHBOARD</h1>
        <p className="text-xs text-zinc-400 font-mono mt-1">Visual intelligence insights and trend analysis</p>
      </div>

      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="glass-panel tactical-border p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-5 h-5 text-[#00FF9D]" />
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">Total Articles</span>
            </div>
            <p className="text-3xl font-bold font-mono">140</p>
            <p className="text-xs text-zinc-500 mt-1">+12% from last week</p>
          </div>
          <div className="glass-panel tactical-border p-4">
            <div className="flex items-center justify-between mb-2">
              <Activity className="w-5 h-5 text-[#00F0FF]" />
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">Active Alerts</span>
            </div>
            <p className="text-3xl font-bold font-mono">24</p>
            <p className="text-xs text-zinc-500 mt-1">8 triggered today</p>
          </div>
          <div className="glass-panel tactical-border p-4">
            <div className="flex items-center justify-between mb-2">
              <Globe className="w-5 h-5 text-[#FFD60A]" />
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">Regions Monitored</span>
            </div>
            <p className="text-3xl font-bold font-mono">5</p>
            <p className="text-xs text-zinc-500 mt-1">Global coverage</p>
          </div>
          <div className="glass-panel tactical-border p-4">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
              <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">High Threats</span>
            </div>
            <p className="text-3xl font-bold font-mono">23</p>
            <p className="text-xs text-zinc-500 mt-1">5 critical incidents</p>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* News Trends */}
          <div className="glass-panel tactical-border p-4">
            <h3 className="text-sm font-bold uppercase mb-4" style={{ fontFamily: 'Barlow Condensed' }}>News Trends (7 Days)</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={data.news_trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="date" stroke="#71717a" style={{ fontSize: '10px', fontFamily: 'JetBrains Mono' }} />
                <YAxis stroke="#71717a" style={{ fontSize: '10px', fontFamily: 'JetBrains Mono' }} />
                <Tooltip
                  contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '2px' }}
                  labelStyle={{ color: '#FAFAFA', fontFamily: 'JetBrains Mono', fontSize: '11px' }}
                />
                <Line type="monotone" dataKey="count" stroke="#00FF9D" strokeWidth={2} dot={{ fill: '#00FF9D' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Regional Distribution */}
          <div className="glass-panel tactical-border p-4">
            <h3 className="text-sm font-bold uppercase mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Regional Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.regional_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="region" stroke="#71717a" style={{ fontSize: '10px', fontFamily: 'JetBrains Mono' }} />
                <YAxis stroke="#71717a" style={{ fontSize: '10px', fontFamily: 'JetBrains Mono' }} />
                <Tooltip
                  contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '2px' }}
                  labelStyle={{ color: '#FAFAFA', fontFamily: 'JetBrains Mono', fontSize: '11px' }}
                />
                <Bar dataKey="count" fill="#00FF9D" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Threat Levels */}
          <div className="glass-panel tactical-border p-4">
            <h3 className="text-sm font-bold uppercase mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Threat Level Distribution</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={data.threat_levels}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {data.threat_levels.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '2px' }}
                  labelStyle={{ color: '#FAFAFA', fontFamily: 'JetBrains Mono', fontSize: '11px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Top Sources */}
          <div className="glass-panel tactical-border p-4">
            <h3 className="text-sm font-bold uppercase mb-4" style={{ fontFamily: 'Barlow Condensed' }}>Top News Sources</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.top_sources} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis type="number" stroke="#71717a" style={{ fontSize: '10px', fontFamily: 'JetBrains Mono' }} />
                <YAxis dataKey="source" type="category" stroke="#71717a" style={{ fontSize: '10px', fontFamily: 'JetBrains Mono' }} />
                <Tooltip
                  contentStyle={{ background: '#0A0A0A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '2px' }}
                  labelStyle={{ color: '#FAFAFA', fontFamily: 'JetBrains Mono', fontSize: '11px' }}
                />
                <Bar dataKey="count" fill="#00F0FF" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}